// src/utils/fileUrl.js
// The ONE browser-side way to get a URL for a stored file. Replaces every inline browser-side
// signed-URL call — because after the R2 cutover
// signing can only happen server-side (the credentials must never reach the bundle). It POSTs to
// /api/download-sign with the staff's Supabase JWT; the endpoint verifies active staff and returns a
// short-lived presigned R2 URL.
//
// Returns the URL string, or THROWS with a readable reason. Callers must handle the throw/null and not
// assume a URL came back — same "check the result, don't assume success" discipline as the rest of the app.
import { supabase } from '../supabase';

// `action` is the INTENT, recorded in the audit: 'preview' (a pane/thumbnail on render), 'open' (new
// tab), or 'download' (a deliberate save). Pass it HONESTLY per call site — a preview is not a download.
// Anything omitted/unknown is logged server-side as 'unspecified' (a visible gap, never null).
//
// ⛔ Cache key is `action + path`, NOT path alone — DO NOT "optimize" it back to path-only. It LOOKS
// redundant (the signed URL is identical for any action on the same file), but the key is exactly what
// forces a distinct action to re-hit the endpoint and produce its audit row. Path-only would let a
// 'preview' cache the URL and a later 'download' reuse it SILENTLY — dropping real downloads from the
// audit trail invisibly, which is the precise failure the audit exists to catch. Keeping action in the
// key still caches the high-volume case (same file, same action: list re-render, reopened preview,
// runaway loop) — that's the volume win and why there's no server rate limit. In-flight de-dup (below)
// collapses a concurrent burst for the same file+action into one request.
const SAFETY_MS = 30 * 1000;         // re-sign this long BEFORE expiry, so a returned URL always has runway
const _cache = new Map();
const _inflight = new Map();

export async function fileUrl(storagePath, action = 'unspecified') {
  if (!storagePath) throw new Error('fileUrl: no storage_path');
  const key = `${action}::${storagePath}`;

  const hit = _cache.get(key);
  if (hit && hit.expiresAt - Date.now() > SAFETY_MS) return hit.url;

  const pending = _inflight.get(key);   // a concurrent burst for the same file+action shares one request
  if (pending) return pending;

  const p = (async () => {
    const { data: { session } } = await supabase.auth.getSession();
    const token = session && session.access_token;
    if (!token) throw new Error('fileUrl: not signed in');

    const resp = await fetch('/api/download-sign', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ storage_path: storagePath, action }),
    });
    if (!resp.ok) {
      let reason = String(resp.status);
      try { const j = await resp.json(); reason = j.error || reason; } catch { /* non-json body */ }
      throw new Error(`fileUrl: ${reason}`);
    }
    const { url, expires_in } = await resp.json();
    if (!url) throw new Error('fileUrl: no url returned');
    _cache.set(key, { url, expiresAt: Date.now() + (Number(expires_in) || 300) * 1000 });
    return url;
  })().finally(() => _inflight.delete(key));

  _inflight.set(key, p);
  return p;
}
